import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Stack } from '@mui/system';
import type { Option } from '@openx/types/options.ts';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ListImperativeAPI } from 'react-window';
import type { AutocompleteConfigurableFilter } from '../../types.ts';
import { AutocompleteSelectorListbox, type AutocompleteSelectorListboxProps } from './AutocompleteSelectorListbox.tsx';

export type AutocompleteSelectorVirtualizedRef = {
  closeDropdown: () => void;
  isDropdownOpen: boolean;
};

type AutocompleteSelectorVirtualizedProps = {
  options?: Option[];
  selectedOptions?: string[];
  onOptionChange: (options: string[]) => void;
  config?: AutocompleteConfigurableFilter<string>['config'];
  isDisabled?: boolean;
};

const getAutocompleteDropdownStyle = (dropdownHeight?: string) => {
  if (!dropdownHeight) return {};

  return {
    sx: {
      '> ul': {
        maxHeight: dropdownHeight,
        overflowY: 'auto',
      },
    },
  };
};

export const AutocompleteSelectorVirtualized = forwardRef<
  AutocompleteSelectorVirtualizedRef,
  AutocompleteSelectorVirtualizedProps
>(({ options = [], selectedOptions = [], onOptionChange, config, isDisabled }, ref) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(selectedOptions);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const { emptyPlaceholder, selectedPlaceholder } = {
    emptyPlaceholder: `[ ${t('Select option')} ]`,
    selectedPlaceholder: `[ ${t('Select or Search')} ]`,
    ...(config || {}),
  };

  const listRef = useRef<ListImperativeAPI | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      isDropdownOpen: open,
      closeDropdown: () => setOpen(false),
    }),
    [open]
  );

  // biome-ignore-start lint/correctness/useExhaustiveDependencies: selectedIds are intentionally omitted in dependency list
  useEffect(() => {
    if (selectedIds.join(',') !== selectedOptions.join(',')) {
      setSelectedIds(selectedOptions);
    }
  }, [selectedOptions]);
  // biome-ignore-end lint/correctness/useExhaustiveDependencies: selectedIds are intentionally omitted in dependency list

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: Option[]) => {
      const newIds = newValue.map(o => o.id);
      setSelectedIds(newIds);
      onOptionChange(newIds);
    },
    [onOptionChange]
  );

  const handleDelete = useCallback(
    (idToDelete: string) => () => {
      const newIds = selectedIds.filter(id => id !== idToDelete);
      setSelectedIds(newIds);
      onOptionChange(newIds);
    },
    [selectedIds, onOptionChange]
  );

  const handleOptionIconClick = useCallback(
    (event: React.SyntheticEvent, selected: boolean, id: string) => {
      event.stopPropagation();
      event.preventDefault();
      let newIds: string[];
      if (selected) {
        newIds = selectedIds.filter(itemId => itemId !== id);
      } else {
        newIds = [...selectedIds, id];
      }
      setSelectedIds(newIds);
      onOptionChange(newIds);
    },
    [selectedIds, onOptionChange]
  );

  const selectedOptionsFull = useMemo(
    () => selectedIds.map(id => options.find(o => o.id === id)).filter((o): o is Option => o !== undefined),
    [selectedIds, options]
  );

  /**
   * After opening the dropdown, scroll to the first selected item (if any).
   * This helps ensure a good UX (so user sees where they are).
   */
  const scrollToSelected = useCallback(() => {
    if (!listRef.current) return;
    if (selectedOptionsFull.length === 0) {
      listRef.current.element?.scrollTo({ top: 0 });
      return;
    }
    const firstSel = selectedOptionsFull[0];
    const index = options.findIndex(o => o.id === firstSel.id);
    if (index >= 0) {
      listRef.current.scrollToRow({ index });
    }
  }, [selectedOptionsFull, options]);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        scrollToSelected();
      }, 0);
    }
  }, [open, scrollToSelected]);

  return (
    <Autocomplete
      data-test="autocomplete-filter-selector"
      multiple
      id="autocomplete-selector"
      disabled={isDisabled}
      loading={config?.isLoading}
      disableCloseOnSelect
      options={options}
      getOptionLabel={option => option.name}
      value={selectedOptionsFull}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onChange={handleChange}
      slots={{ listbox: AutocompleteSelectorListbox }}
      slotProps={{
        paper: getAutocompleteDropdownStyle(config?.dropdownHeight),
        listbox: { dropdownHeight: config?.dropdownHeight } as AutocompleteSelectorListboxProps,
      }}
      renderInput={params => {
        const { slotProps: paramsSlotProps, ...rest } = params;
        return (
          <TextField
            data-test="autocomplete-filter-input"
            {...rest}
            variant="outlined"
            placeholder={selectedOptionsFull.length === 0 ? emptyPlaceholder : selectedPlaceholder}
            sx={{
              '& .MuiAutocomplete-endAdornment': {
                right: '0 !important',
              },
              '& .MuiOutlinedInput-root': {
                paddingTop: '2px !important',
                paddingBottom: '0px !important',
                paddingLeft: '4px !important',
                right: '0 !important',
              },
            }}
            slotProps={{
              input: {
                ...paramsSlotProps.input,
                startAdornment: (
                  <>
                    {selectedOptionsFull.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip
                          label={selectedOptionsFull[0].name}
                          onDelete={handleDelete(selectedOptionsFull[0].id)}
                          sx={{ height: 24 }}
                          deleteIcon={
                            <IconButton size="large" aria-label="remove-option">
                              <CloseIcon data-test="chip-delete" />
                            </IconButton>
                          }
                        />
                        {selectedOptionsFull.length > 1 && (
                          <Box sx={{ ml: 0.5 }}>
                            <Typography>+{selectedOptionsFull.length - 1}</Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                    {paramsSlotProps.input.startAdornment}
                  </>
                ),
              },
              htmlInput: { ...paramsSlotProps.htmlInput },
            }}
          />
        );
      }}
      renderOption={(props, option, { selected }) => {
        const { key, ...rest } = props;
        return (
          <ListItemButton
            key={key}
            component="li"
            {...rest}
            onClick={e => handleOptionIconClick(e, selected, option.id)}
            sx={theme => ({
              minHeight: 48,
              paddingTop: '6px',
              paddingBottom: '6px',
              [theme.breakpoints.up('sm')]: {
                minHeight: 'auto',
              },
              '&:hover': { color: 'inherit' },
              '.MuiListItemIcon-root': { minWidth: 'auto' },
            })}
          >
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Typography
                data-test={`autocomplete-dropdown-option-${option.name.toLowerCase()}`}
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
              >
                {option.name}
              </Typography>
              <ListItemIcon sx={{ ml: 'auto' }}>{selected ? <RemoveIcon /> : <AddIcon />}</ListItemIcon>
            </Stack>
          </ListItemButton>
        );
      }}
      renderValue={() => null}
    />
  );
});
